import { toThousandths } from '@platform/design-system';
import { SUPPORTED_LOCALES } from '@platform/i18n';
import { combinationKey } from './options.ts';
import { decimalOf, knownSplits, millimetresOf, type ProductFormValues } from './product-form.ts';
import type { CreateProductBody, Product } from './product.ts';
import { effectiveSku } from './product-variants.ts';

const orNull = (text: string): string | null => (text.trim() === '' ? null : text.trim());

const optionalAmount = (text: string): string | null => (text.trim() === '' ? null : decimalOf(text));

export type BodyContext = {
  readonly product: Product | null;
  readonly canSeeCost: boolean;
};

export const bodyFromForm = (
  values: ProductFormValues,
  { product, canSeeCost }: BodyContext,
): CreateProductBody => {
  const otherCategories = product?.categoryIds.slice(1) ?? [];
  const weight = orNull(values.weightKg);
  return {
    reference: orNull(values.reference),
    warrantyMonths: values.warrantyMonths.trim() === '' ? null : Number(values.warrantyMonths),
    subsidiaryId: orNull(values.subsidiaryId),
    translations: SUPPORTED_LOCALES.filter((locale) => values.translations[locale].name.trim() !== '').map(
      (locale) => ({
        locale,
        name: values.translations[locale].name.trim(),
        description: orNull(values.translations[locale].description),
      }),
    ),
    categoryIds: values.categoryId === '' ? otherCategories : [values.categoryId, ...otherCategories],
    variants: values.variants
      .filter((variant) => variant.enabled)
      .map((variant, position) => ({
        id: variant.variantId,
        optionValueIds: variant.valueIds,
        sku: effectiveSku(variant, values.reference),
        barcode: orNull(variant.barcode),
        price: decimalOf(values.perVariantPrice ? variant.price : values.price),
        compareAtPrice: values.promo ? decimalOf(values.regularPrice) : null,
        costPrice: canSeeCost ? optionalAmount(values.perVariantPrice ? variant.cost : values.cost) : null,
        weightG: weight === null ? null : toThousandths(decimalOf(weight)),
        lengthMm: millimetresOf(values.lengthCm),
        widthMm: millimetresOf(values.widthCm),
        heightMm: millimetresOf(values.heightCm),
        position,
      })),
    media: values.media.map((entry) => ({ mediaId: entry.mediaId, alt: orNull(entry.alt) })),
  };
};

export type InitialStockLine = {
  readonly variantId: string;
  readonly locationId: string;
  readonly quantity: number;
  readonly lowStockThreshold: number | null;
};

const wholeNumberOrNull = (text: string | undefined): number | null =>
  text === undefined || text.trim() === '' ? null : Number(text.trim());

export const splitEvenly = (total: number, parts: number): number[] => {
  const base = Math.floor(total / parts);
  const remainder = total % parts;
  return Array.from({ length: parts }, (_, index) => base + (index < remainder ? 1 : 0));
};

const savedIdsOf = (saved: Product): ReadonlyMap<string, string> =>
  new Map(saved.variants.map((variant) => [combinationKey(variant.optionValueIds), variant.id]));

type StockedVariant = { readonly variantId: string; readonly combination: string };

const quantitiesOf = (values: ProductFormValues, locationId: string, variants: readonly StockedVariant[]) => {
  const splits = knownSplits(values, locationId);
  if (splits.length === 0)
    return splitEvenly(wholeNumberOrNull(values.stockTotals[locationId]) ?? 0, variants.length);
  return variants.map(({ combination }) =>
    splits
      .filter((split) => split.combination === combination)
      .reduce((sum, split) => sum + (wholeNumberOrNull(split.quantity) ?? 0), 0),
  );
};

export const initialStockLines = (
  values: ProductFormValues,
  saved: Product,
  acceptsInitialStock: (variantId: string) => boolean,
): InitialStockLine[] => {
  const savedIds = savedIdsOf(saved);
  const variants = values.variants
    .filter((row) => row.enabled)
    .flatMap((row): StockedVariant[] => {
      const combination = combinationKey(row.valueIds);
      const variantId = savedIds.get(combination);
      return variantId === undefined || !acceptsInitialStock(variantId) ? [] : [{ variantId, combination }];
    });
  if (variants.length === 0) return [];
  const locationIds = new Set([...Object.keys(values.stockTotals), ...Object.keys(values.thresholds)]);
  return [...locationIds].flatMap((locationId) => {
    const quantities = quantitiesOf(values, locationId, variants);
    const lowStockThreshold = wholeNumberOrNull(values.thresholds[locationId]);
    return variants
      .map(({ variantId }, position) => ({
        variantId,
        locationId,
        quantity: quantities[position] ?? 0,
        lowStockThreshold,
      }))
      .filter((line) => line.quantity > 0 || line.lowStockThreshold !== null);
  });
};

export const withSavedIds = (values: ProductFormValues, saved: Product): ProductFormValues => {
  const savedIds = savedIdsOf(saved);
  return {
    ...values,
    variants: values.variants.map((row) => ({
      ...row,
      variantId: savedIds.get(combinationKey(row.valueIds)) ?? row.variantId,
    })),
  };
};
